import { ENV } from '~/config/article/env';

/** Performance API markers */
export const markers = {
  adConfigLoadEnd: 'Advertisement config load end',
  adConfigLoadError: 'Advertisement config load error',
  adConfigLoadStart: 'Advertisement config load start',
  adEnd: 'Advertisement End',
  adStart: 'Advertisement Start',
  commsCreatorEnd: 'Communication creator end',
  commsCreatorStart: 'Communication creator start',
  geoRequestCache: 'Geo request retrieval from cache',
  geoRequestEnd: 'Geo request end',
  geoRequestError: 'Geo request error',
  geoRequestStart: 'Geo request start',
  platformStart: 'Start',
  prebidLoadEnd: 'Prebid loading end',
  prebidLoadError: 'Prebid loading error',
  prebidLoadStart: 'Prebid loading start',
  websocketOpened: 'Websocket connection opened',
  widgetsCreatorEnd: 'Widget creator end',
  widgetsCreatorStart: 'Widget creator start',
};

/** Performance API measures */
export const measures = {
  adConfigStartToError: 'Advertisement config load start -> load error',
  adStartToConfigLoadEnd: 'Advertisement start -> ad config load end',
  adStartToConfigLoadStart: 'Advertisement start -> ad config load start',
  adStartToEnd: 'Advertisement start -> end',
  adStartToPrebidLoadStart: 'Advertisement start -> prebid script load start',
  commsStartToEnd: 'Communication creator start -> end',
  geoStartToEnd: 'Geo request start -> geo request end',
  geoStartToError: 'Geo request start -> geo request error',
  platformStartToAdConfigLoadEnd: 'Platform start -> ad config load end',
  platformStartToAdConfigLoadError: 'Platform start -> ad config load error',
  platformStartToAdEnd: 'Platform start -> advertisement end',
  platformStartToAdStart: 'Platform start -> advertisement start',
  platformStartToCommsEnd: 'Platform start -> communication creator end',
  platformStartToCommsStart: 'Platform start -> communication creator start',
  platformStartToGeoCache: 'Platform start -> geo request cache retrieval',
  platformStartToGeoEnd: 'Platform start -> geo request end',
  platformStartToGeoError: 'Platform start -> geo request error',
  platformStartToGeoStart: 'Platform start -> geo request start',
  platformStartToLog: 'Platform start -> version log',
  platformStartToPrebidError: 'Platform start -> prebid script load error',
  platformStartToPrebidLoad: 'Platform start -> prebid script load end',
  platformStartToPrebidLoadStart: 'Platform start -> prebid script load start',
  platformStartToWebsocketOpen: 'Platform start -> websocket connection opened',
  platformStartToWidgetsEnd: 'Platform start -> widget creation end',
  platformStartToWidgetsStart: 'Platform start -> widget creation start',
  prebidStartToEnd: 'Prebid loading start -> end',
  prebidStartToError: 'Prebid loading start -> error',
  widgetStartToEnd: 'Widget creation start -> end',
};
const searchParams = new URLSearchParams(window.location.search);
/**
 * Runs the debug functions if the query param is given
 * @param func
 * @param {...any} args
 */
function runIfDebugEnabled(func: (...funcargs: any[]) => any, ...args: any[]) {
  if (searchParams.get('vkdebug') === 'true') {
    func(...args);
  }
}

/**
 * @param name
 */
function startProfiler(name: string = 'Vuukle Platform') {
  console.profile(name);
}

/**
 * @param name
 */
function endProfiler(name: string = 'Vuukle Platform') {
  console.profileEnd(name);
}

// function startTimer(name: string = 'timer') {
//   console.time(name);
// }

// function endTimer(name: string = 'timer') {
//   console.timeEnd(name);
// }

// function outputTimer(name: string = 'timer') {
//   console.timeLog(name);
// }

/**
 * @param name
 */
function createPerformanceMarker(name: string = 'mark') {
  performance.mark(name);
}

// function outputAllMeasures() {
//   performance.getEntriesByType('measure').forEach((entry) => {
//     // tslint:disable-next-line
//     console.info(`Vuukle Platform measure - ${entry.name}: ${entry.duration}`);
//   });
// }

/**
 * @param num
 */
function pad(num: number) {
  return num.toString().padStart(2, '0');
}

/**
 * Decorate output logs
 * @param args
 * @param prefix
 */
function decorateLog(args: string[], prefix: any) {
  args = [].slice.call(args);
  if (prefix) {
    args.unshift(prefix);
  }
  let bg = '#1e88e5';
  let rbg = '#' + Math.floor(Math.random() * 0x1000000).toString(16);
  const now = new Date();
  const time = `[${now.getHours().toString()}:${pad(now.getMinutes())}:${pad(now.getSeconds())}]`;
  args.unshift(`display: inline-block; color: #fff; background: ${rbg}; padding: 1px 4px; border-radius: 3px;`);
  args.unshift(`display: inline-block; color: #fff; background: ${bg}; padding: 1px 4px; border-radius: 3px;`);
  args.unshift(`${time} %cVuukle Platform%c`);
  return args;
}

/**
 * @param name
 * @param type
 */
function outputSpecificMeasureOrMark(name: string = 'mark', type: string = 'mark') {
  performance.getEntriesByName(name, type).forEach((entry) => {
    // tslint:disable-next-line
    console.log.apply(console, decorateLog([], `${type} - ${entry.name}: ${entry.duration.toFixed(2)}ms`));
  });
}

/**
 * @param measureName
 * @param firstMark
 * @param secondMark
 */
function measureMarks(measureName: string = 'measure', firstMark?: string, secondMark?: string) {
  performance.measure(measureName, firstMark, secondMark);
  outputSpecificMeasureOrMark(measureName, 'measure');
}

export const profile = (name: string) => runIfDebugEnabled(startProfiler, name);
export const profileEnd = (name: string) => runIfDebugEnabled(endProfiler, name);

// export const time = (name: string) => runIfDebugEnabled(startTimer, name);
// export const timeEnd = (name: string) => runIfDebugEnabled(endTimer, name);
// export const timeLog = (name: string) => runIfDebugEnabled(outputTimer, name);

export const createMarker = (name: string) => runIfDebugEnabled(createPerformanceMarker, name);
export const measureTimeBetweenMarks = (measureName: string, firstMark?: string, secondMark?: string) =>
  runIfDebugEnabled(measureMarks, measureName, firstMark, secondMark);
export const logMarkOrMeasure = (name: string, type: string) =>
  runIfDebugEnabled(outputSpecificMeasureOrMark, name, type);

export const debugLog = (logType: 'log' | 'trace' | 'info' | 'error' | 'warn', ...args: any) => {
  if (ENV.IS_STAGING || ENV.NODE_ENV === 'development') {
    console[logType](...args);
  }
};

export const vdebugLog = (logType: 'log' | 'trace' | 'info' | 'error' | 'warn', logToDev: boolean, ...args: any) => {
  if (window.location.search.includes('vdebug')) {
    console[logType](...args);
  } else {
    if (logToDev) {
      debugLog(logType, ...args);
    }
  }
};
